import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getDefaultHeaders } from "utils/helpers";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import { Manufacturer, ManufacturerForm, ManufacturerListResponse, ManufacturerReorderDirection } from "../types";
import {
  selectManufacturerForm,
  selectManufacturersList,
  selectManufacturersPage,
  selectManufacturersPerPage,
  selectSelectedId,
} from "./selector";
import { manufacturerActions } from "./slice";

let isFetchingList = false;

function* handleGetManufacturersList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectManufacturersPage);
    const perPage: number = yield select(selectManufacturersPerPage);

    const response: ManufacturerListResponse = yield call(
      request,
      `${API_URL}/manufacturer?page=${page}&perPage=${perPage}`,
      { method: "GET", headers: getDefaultHeaders() }
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(manufacturerActions.getManufacturersListSuccess(payload));
    } else {
      yield put(manufacturerActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const page: number = yield select(selectManufacturersPage);
    if (page === 1) {
      yield put(manufacturerActions.getManufacturersListFailure((error as Error).message));
    } else {
      yield put(manufacturerActions.getNextPageItemsFailure((error as Error).message));
    }
  } finally {
    isFetchingList = false;
  }
}

function* createManufacturer() {
  try {
    const form: ManufacturerForm = yield select(selectManufacturerForm);

    const options = {
      method: "POST",
      headers: getDefaultHeaders(),
      body: JSON.stringify(form),
    };
    const response = yield call(request, `${API_URL}/manufacturer`, options);
    yield put(manufacturerActions.createManufacturerSuccess(response.data));
  } catch (error) {
    yield put(manufacturerActions.createManufacturerFailure());
  }
}

function* deleteManufacturerSaga() {
  try {
    const id: number = yield select(selectSelectedId);
    const options = { method: "DELETE", headers: getDefaultHeaders() };
    yield call(request, `${API_URL}/manufacturer/${id}`, options);
    yield put(manufacturerActions.deleteManufacturerSuccess(id));
  } catch (error) {
    yield put(manufacturerActions.deleteManufacturerFailure());
  }
}

function* reorderManufacturerSaga(
  action: PayloadAction<{ id: number; direction: ManufacturerReorderDirection }>
) {
  const { id, direction } = action.payload;
  const list: Manufacturer[] = yield select(selectManufacturersList);

  const currentIndex = list.findIndex(
    (manufacturer) => manufacturer.menufecturerID === id
  );
  const targetIndex =
    direction === "up" ? currentIndex - 1 : currentIndex + 1;
  const targetRow = list[targetIndex];

  if (currentIndex === -1 || !targetRow) {
    yield put(manufacturerActions.reorderManufacturerFailure(""));
    return;
  }

  try {
    yield call(request, `${API_URL}/manufacturer/${id}/reorder`, {
      method: "PATCH",
      headers: getDefaultHeaders(),
      body: JSON.stringify({ targetPriority: targetRow.priority }),
    });
    yield put(manufacturerActions.reorderManufacturerSuccess());
    yield put(manufacturerActions.getManufacturersList());
  } catch (error) {
    yield put(
      manufacturerActions.reorderManufacturerFailure((error as Error).message)
    );
  }
}

export function* manufacturerSaga() {
  yield takeLeading(manufacturerActions.getManufacturersList.type, handleGetManufacturersList);
  yield takeLeading(manufacturerActions.getNextPageItems.type, handleGetManufacturersList);
  yield takeLatest(manufacturerActions.createManufacturer.type, createManufacturer);
  yield takeLatest(manufacturerActions.deleteManufacturer.type, deleteManufacturerSaga);
  yield takeLatest(manufacturerActions.reorderManufacturer.type, reorderManufacturerSaga);
}
